import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import {
  Button,
  Form,
  Modal,
  ModalBody,
  OverlayTrigger,
  ProgressBar,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import { useLocation, useNavigate } from "react-router";
import { getSurvey } from "../../services/getFunctions";
import { setSurvey } from "../../services/postFunctions";
import {
  ResponseOption,
  SurveyDimension,
  SurveyItem,
} from "../../types/surveyTypes";
import { FormControlElement } from "../../types/uiTypes";
import NavigationBar from "../navigation/NavigationBar";
import { useAlertHelpers } from "../../helper/Hooks/useAlertHelpers";
import { usePartialState } from "../../helper/Hooks/usePartialState";
import { useRealNavigation } from "../../helper/Hooks/useRealNavigation";
import temperatureScale from "../../img/temperature_scale.png";
import { formatLocalDate } from "../../helper/dateHelpers";

interface Group {
  head: boolean;
  value: number;
  id: number;
  group: boolean;
  type: string;
  content: string;
  response_options: ResponseOption[];
}

interface ItemResponse {
  id: number;
  label: string;
  time_taken: number;
  value: string;
  optionalQuestion: boolean;
}

interface RunSurveyState {
  date: Date | null;
  items: SurveyItem[];
  iGroups: Group[];
  surveyDimension: SurveyDimension | undefined;
  waitModalOpen: boolean;
  itemResponses: ItemResponse[];
  progressBarStatus: number;
  highlightItemIndex: number;
  sendingSurvey: boolean;
  endOfSurvey: boolean;
}

interface RunSurveyProps {
  title: string;
}

const RunSurvey = ({ title }: RunSurveyProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  //ref to prevent doule submission of auto-submit items
  const isSubmittingRef = useRef(false);

  const { showAlertModal } = useAlertHelpers();
  const { checkIsRealNavigation } = useRealNavigation(location.key, 300);

  const initialState: RunSurveyState = {
    date: null,
    items: [],
    iGroups: [],
    surveyDimension: undefined,
    waitModalOpen: false,
    itemResponses: [],
    progressBarStatus: 0,
    highlightItemIndex: -1,
    sendingSurvey: false,
    endOfSurvey: false,
  };

  const [state, updateState] = usePartialState(initialState);

  /**
   * Method to open the wait modal with animated progress bar
   * used to show the loading status while processing survey response
   */
  const openWaitModal = (callback?: () => void) => {
    //ensure the progress bar starts at 0 when the modal opens
    updateState({ waitModalOpen: true, progressBarStatus: 0 });
    let countdown = 0;

    //clear potential timer
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    progressInterval.current = setInterval(() => {
      if (countdown < 98) {
        // Progress stops at 98% to give visual feedback before completion
        countdown++;
        updateState({ progressBarStatus: countdown });
      } else {
        clearInterval(progressInterval.current!);
        progressInterval.current = null;

        if (callback) {
          try {
            callback();
          } catch (error) {
            console.error("Wait modal callback error:", error);
          }
        }
      }
    }, 80); //Interval timing for animation (~12 updates per second)
  };

  /**
   * Closes the wait modal and clears the progress interval.
   */
  const closeModal = () => {
    updateState({ waitModalOpen: false });
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
  };

  /*
   *  Ends the survey by updating the progress bar status, closing the modal, and navigating to the patients page.
   */
  const endSurvey = () => {
    updateState({ progressBarStatus: 100, endOfSurvey: true });
    openWaitModal(() => {
      navigate("/patients");
      closeModal();
    });
  };

  /**
   * This function calls `getSurvey` to get the survey data.
   * Depending on the response's status code:
   * - If the status code is 204, it sets the end of the survey state.
   * - If the status code is 500, it shows an alert modal with an error message and navigates to the '/patients' route.
   * - Otherwise, it processes the survey data by updating the items and survey dimension.
   * If an error occurs during the fetch, it shows an alert modal with an error message.
   */
  const tryGetSurvey = useCallback(async () => {
    try {
      const res = await getSurvey();
      if (res.statusCode === 500) {
        showAlertModal("Entschuldigung. Da ist etwas schiefgelaufen.");
        navigate("/patients");
      } else if (res.statusCode === 204) {
        updateState({ endOfSurvey: true });
      } else {
        const newItems = [...res.survey.items];
        updateState({
          items: newItems,
          surveyDimension: res.survey.dimension,
        });
      }
    } catch (e) {
      showAlertModal("Entschuldigung. Da ist etwas schiefgelaufen.");
    }
  }, [navigate, updateState, showAlertModal]);

  /**
   * Sends survey responses and handles the survey update process.
   */
  const sendSurveyResponses = useCallback(
    async (items: ItemResponse[]) => {
      try {
        updateState({ sendingSurvey: true });
        await setSurvey(items);
        await tryGetSurvey();
        updateState({ sendingSurvey: false });
      } catch (e) {
        updateState({ sendingSurvey: false });
      }
    },
    [updateState, tryGetSurvey],
  );

  /**
   * Initializes all survey items based on the type (manditory, optinal, email, info)
   */
  const initialEmptyResponses = useCallback(
    (items) => {
      const responses: ItemResponse[] = [];

      for (let i = 0; i < items.length; i++) {
        if (
          // manditory question types
          items[i].type === "INFO_AUTOSUBMIT" ||
          items[i].type === "QUESTION_HORIZONTAL_RADIO" ||
          items[i].type === "QUESTION_VERTICAL_RADIO" ||
          items[i].type === "QUESTION_HORIZONTAL_RADIO_ENFORCED" ||
          items[i].type === "QUESTION_SELECT" ||
          items[i].type === "QUESTION_INTEGER"
        ) {
          responses[i] = {
            id: items[i].id,
            label: "",
            time_taken: 0,
            value: "",
            optionalQuestion: false,
          };
        } else if (
          // optional question types
          items[i].type === "INFO" ||
          items[i].type === "EMAIL_NOTIFICATION_PRE" ||
          items[i].type === "EMAIL_NOTIFICATION_INTERMEDIATE" ||
          items[i].type === "EMAIL_NOTIFICATION_POST" ||
          items[i].type === "EMAIL_NOTIFICATION_FOLLOW_UP" ||
          items[i].type === "QUESTION_TEXT_OPTIONAL" ||
          items[i].type === "QUESTION_TEXT_AREA_OPTIONAL"
        ) {
          responses[i] = {
            id: items[i].id,
            label: "",
            time_taken: 0,
            value: "",
            optionalQuestion: true,
          };
        } else {
          /* empty */
        }
      }
      console.log("Setting itemResponses:", responses);
      updateState({ itemResponses: responses });
    },
    [updateState],
  );

  /**
   * Extract scripts in html-response from item "INFO" and "INFO_AUTOSUBMIT".
   */
  const extractScript = useCallback((items) => {
    for (let i = 0; i < items.length; i++) {
      if (items[i].type === "INFO" || items[i].type === "INFO_AUTOSUBMIT") {
        if (items[i].content != null) {
          const content = items[i].content;
          const scriptMatch = /<script\s*.*>[\s\S]*<\/script>/gi.exec(content);
          if (scriptMatch) {
            const [extract] = scriptMatch;
            const cleanScript = extract.replace(/<\/?script\s*.*>/gm, "");
            try {
              // eslint-disable-next-line
              window.eval(cleanScript);
            } catch (error) {
              console.error("Error executing script:", error);
            }
          }
        }
      }
    }
  }, []);

  /**Check if Items with type "QUESTION_HORIZONTAL_RADIO" have the same response_options.
   * Save them in state list itemGroups with parameter group: true||false.
   * The first item in group needs the parameter head: true.
   * @param items - Array of survey items to process
   * @param currentGroups - Current iGroups state (passed as parameter to avoid dependency)
   */
  const checkGroupOptions = useCallback(
    (items, currentGroups: Group[] = []) => {
      //for single question_horizontal radio item
      if (items.length === 1 && items[0].type === "QUESTION_HORIZONTAL_RADIO") {
        const groups: Group[] = [...currentGroups];
        groups.push(items[0] as Group);
        groups[0].id = items[0].id;
        groups[0].group = false;
        updateState({ iGroups: groups });
        return;
      }

      const updatedGroups: Group[] = [...currentGroups];

      //first loop: set value = 0 || 1 || 2 || 3
      for (let i = 0; i < items.length; i++) {
        if (!updatedGroups[i]) {
          updatedGroups[i] = {} as Group;
        }

        if (items[i].type !== "QUESTION_HORIZONTAL_RADIO") {
          updatedGroups[i] = { ...items[i] } as Group;
          updatedGroups[i].id = items[i].id;
          updatedGroups[i].value = 0;
        } else if (
          items[i].type === "QUESTION_HORIZONTAL_RADIO" &&
          (!items[i - 1] ||
            items[i - 1].type !== "QUESTION_HORIZONTAL_RADIO") &&
          (!items[i + 1] || items[i + 1].type !== "QUESTION_HORIZONTAL_RADIO")
        ) {
          //single question
          updatedGroups[i] = { ...updatedGroups[i], ...items[i] } as Group;
          updatedGroups[i].id = items[i].id;
          updatedGroups[i].value = 1;
          updatedGroups[i].group = false;
        } else if (
          items[i].type === "QUESTION_HORIZONTAL_RADIO" &&
          items[i + 1] &&
          items[i + 1].type === "QUESTION_HORIZONTAL_RADIO"
        ) {
          updatedGroups[i] = { ...updatedGroups[i], ...items[i] } as Group;
          updatedGroups[i].id = items[i].id;
          updatedGroups[i].value = 2;
        } else if (
          items[i].type === "QUESTION_HORIZONTAL_RADIO" &&
          items[i - 1] &&
          items[i - 1].type === "QUESTION_HORIZONTAL_RADIO" &&
          (!items[i + 1] || items[i + 1].type !== "QUESTION_HORIZONTAL_RADIO")
        ) {
          updatedGroups[i] = { ...updatedGroups[i], ...items[i] } as Group;
          updatedGroups[i].id = items[i].id;
          updatedGroups[i].value = 3;
        }
      }

      //for loop to loop over items-list with inner for-loop to check response_options
      for (let j = 0; j < items.length; j++) {
        let group = 0;

        if (updatedGroups[j]) {
          if (updatedGroups[j].value === 2) {
            for (let l = 0; l < items[j].response_options.length; l++) {
              if (
                items[j + 1] &&
                items[j].response_options.length ===
                  items[j + 1].response_options.length &&
                items[j].response_options[l].label ===
                  items[j + 1].response_options[l].label
              ) {
                group++;
              }
            }
            if (group === items[j].response_options.length) {
              updatedGroups[j] = {
                ...updatedGroups[j],
                group: true,
              };
              updatedGroups[j + 1] = {
                ...updatedGroups[j + 1],
                group: true,
              };
            } else if (updatedGroups[j].group) {
              updatedGroups[j] = {
                ...updatedGroups[j],
                value: 3,
              };
            } else if (!updatedGroups[j].group) {
              updatedGroups[j] = {
                ...updatedGroups[j],
                group: false,
              };
            }
          } else if (updatedGroups[j].value === 3 && !updatedGroups[j].group) {
            updatedGroups[j] = {
              ...updatedGroups[j],
              group: false,
            };
          }
        }
      }

      // mark the first item in each group with head
      for (let k = 0; k < updatedGroups.length; k++) {
        if (updatedGroups[k]) {
          if (
            updatedGroups[k].value === 2 &&
            updatedGroups[k].group &&
            (!updatedGroups[k - 1] ||
              updatedGroups[k - 1].value !== 2 ||
              !updatedGroups[k - 1].group)
          ) {
            updatedGroups[k] = {
              ...updatedGroups[k],
              head: true,
            };
          }
        }
      }

      const needsUpdate =
        JSON.stringify(updatedGroups) !== JSON.stringify(currentGroups);
      if (needsUpdate) {
        updateState({ iGroups: updatedGroups });
      }
    },
    [updateState],
  );

  /**Handle responses from radio buttons and set response state with the right item id
   * @param event {ChangeEvent} onChange delivers the event with the selected radio button
   * @param item {Object} Contains the item with the item id
   * @param index {integer} Index in itemResponses-Array
   */
  const handleRadioBtn = (event, item, index) => {
    const responses = [...state.itemResponses];
    responses[index] = {
      id: item.id,
      label: event.target.dataset.name,
      time_taken: event.timeStamp,
      value: event.target.value,
      optionalQuestion: false,
    };
    updateState({ itemResponses: responses });
  };

  /**Handle responses from question select and set response state with the right item id
   * @param event {ChangeEvent} onChange delivers the event with the selected option
   * @param item {Object} Contains the item with the item id
   * @param index {integer} Index in itemResponses-Array
   */
  const handleQuestionSelect = (event, item, index) => {
    const responses = [...state.itemResponses];
    if (event.target.value !== "Auswählen...") {
      responses[index] = {
        id: item.id,
        label: event.target[event.target.selectedIndex].dataset.name,
        time_taken: event.timeStamp,
        value: event.target.value,
        optionalQuestion: false,
      };
    } else if (event.target.value === "Auswählen...") {
      responses[index] = {
        id: 0,
        label: "",
        time_taken: 0,
        value: "",
        optionalQuestion: false,
      };
    } else return;
    updateState({ itemResponses: responses });
  };

  /**Handle responses from text input and set response state with the right item id
   * @param event {ChangeEvent} onChange delivers the event with the changed text input
   * @param item {Object} Contains the item with the item id
   * @param index {integer} Index in itemResponses-Array
   */
  const handleTextInput = (event, item, index) => {
    const responses = [...state.itemResponses];

    if (item.type.includes("EMAIL") || item.type.includes("OPTIONAL")) {
      // optional text
      responses[index] = {
        id: item.id,
        label: "",
        time_taken: event.timeStamp,
        value: event.target.value,
        optionalQuestion: true,
      };
    } else if (event.target.value !== "") {
      // non-optional text
      responses[index] = {
        id: item.id,
        label: "",
        time_taken: event.timeStamp,
        value: event.target.value,
        optionalQuestion: false,
      };
    } else {
      responses[index] = {
        id: item.id,
        label: "",
        time_taken: 0,
        value: "",
        optionalQuestion: false,
      };
    }

    updateState({ itemResponses: responses });
  };

  /**Handle responses from date input and set response state with the right item id
   * @param date {date} date from datepicker or date input
   * @param item {Object} Contains the item with the item id
   * @param index {integer} Index in itemResponses-Array
   */
  const handleDate = (date, item, index) => {
    updateState({ date });
    const responses = [...state.itemResponses];
    responses[index] = {
      id: item.id,
      label: "",
      time_taken: 0,
      value: formatLocalDate(date),
      optionalQuestion: false,
    };
    updateState({ itemResponses: responses });
  };

  /**Handle responses from integer input and set response state with the right item id.
   * Checks whether the value is an integer. If the value isNaN after parseInt() the state would not be updated.
   * @param event {React.ChangeEvent | React.KeyboardEvent} onChange delivers the event with the changed text input
   * @param item {SurveyItem} Contains the item with the item id
   * @param index {number} Index in itemResponses-Array
   */
  const handleInteger = (
    event: React.KeyboardEvent | React.ChangeEvent,
    item: SurveyItem,
    index: number,
  ) => {
    const numberRegex = /^\d+$/;
    const responses = [...state.itemResponses];
    const input = event.target as HTMLInputElement;
    const strVal = input.value;
    const val = parseInt(strVal);
    if (!handleIntegerError(val, item, input)) {
      return;
    }

    if (event.type === "keydown") {
      const keyboardEvent = event as React.KeyboardEvent<HTMLInputElement>;
      if (
        !numberRegex.test(keyboardEvent.key) &&
        ![
          "Delete",
          "Backspace",
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Tab",
        ].includes(keyboardEvent.key) &&
        event.type === "keydown"
      ) {
        event.nativeEvent.preventDefault();
      }
    }

    if (numberRegex.test(strVal)) {
      responses[index] = {
        id: item.id,
        label: "",
        time_taken: event.timeStamp,
        value: String(val),
        optionalQuestion: false,
      };
    } else if (strVal === "") {
      responses[index] = {
        id: 0,
        label: "",
        time_taken: 0,
        value: "",
        optionalQuestion: false,
      };
    }
    updateState({ itemResponses: responses });
  };

  const handleIntegerError = (
    val: number,
    item: SurveyItem,
    input: HTMLInputElement,
  ): boolean => {
    const highestIntAllowedByBackend = 2147483647; //(2^31-1)
    const strVal = input.value;

    if (val > highestIntAllowedByBackend) {
      input.value = strVal.slice(0, -1);
      showAlertModal(
        "Die von Ihnen eingegebene Zahl ist zu groß. Die höchste erlaubte Zahl ist " +
          highestIntAllowedByBackend,
      );
      return false;
    }
    if (item.type === "QUESTION_RANGE_INDICATOR" && val > 100) {
      input.value = strVal.slice(0, -1);
      showAlertModal(
        "Die von Ihnen eingegebene Zahl ist zu groß. Die höchste erlaubte Zahl ist 100",
      );
      return false;
    }
    return true;
  };

  /**
   * When called searches for a sibling of the event target that is an input element of type=email and if found adds a @ symbol to the end of it's value
   * @param {*} event
   * @param {*} item
   * @param {*} index
   */
  const handleAtSymbol = (event) => {
    const parent = event.target.parentElement;

    const formElement = parent.querySelector("input[type=email]");
    try {
      formElement.value += "@";
    } catch (e) {
      console.error("Error when trying to add @: ", e);
    }
  };

  /**Handle responses from positive double input and set response state with the right item id.
   * The value can be typed in with a "." or ",".
   * Checks whether the value is a positive double. If the value isNaN after parseFloat() or the value is negative the state would not be updated.
   * @param event {ChangeEvent} onChange delivers the event with the changed text input
   * @param item {Object} Contains the item with the item id
   * @param index {integer} Index in itemResponses-Array
   */
  const handlePosDouble = (
    event: React.ChangeEvent<FormControlElement>,
    item: SurveyItem,
    index: number,
  ) => {
    const responses = [...state.itemResponses];
    const val = parseFloat(event.target.value.replace(/,/, "."));
    if (!isNaN(val) && val > 0) {
      responses[index] = {
        id: item.id,
        label: "",
        time_taken: event.timeStamp,
        value: String(val),
        optionalQuestion: false,
      };
      updateState({ itemResponses: responses });
    } else if (event.target.value === "") {
      responses[index] = {
        id: 0,
        label: "",
        time_taken: 0,
        value: "",
        optionalQuestion: false,
      };
    } else {
      showAlertModal("Sie dürfen nur positive Kommazahlen eingeben.");
    }
  };

  /**
   * Method for handle the survey item responses.
   * The time taken is calculated and divided among the items.
   * Then it is checked whether an entry has been created in the list for each item. If not, an alert is displayed to the user.
   * The input data are stringified to JSON.
   * Then send JSON to backend API and handle the response.
   */
  const handleSurveyResponses = async (event) => {
    event.preventDefault();
    updateState({ highlightItemIndex: -1 });

    if (state.endOfSurvey) {
      endSurvey();
    } else if (
      Array.isArray(state.items) &&
      state.items[0]?.type !== "INFO_AUTOSUBMIT"
    ) {
      const count = state.items.length;
      const timeTaken = (event.timeStamp / 1000 / count).toFixed(3);
      let valid = true;
      const responses = [...state.itemResponses];

      const currentDimensionId = state.surveyDimension?.id || "";

      const preambleIdPrefixes = ["hello", "info", "email", "intro"];

      const isRealSurveyStarted = !preambleIdPrefixes.some((prefix) =>
        currentDimensionId.startsWith(prefix),
      );

      for (let i = 0; i < count; i++) {
        if (
          state.items[i].type === "EMAIL_NOTIFICATION_PRE" ||
          state.items[i].type === "EMAIL_NOTIFICATION_INTERMEDIATE" ||
          state.items[i].type === "EMAIL_NOTIFICATION_POST" ||
          state.items[i].type === "EMAIL_NOTIFICATION_FOLLOW_UP"
        ) {
          const email = responses[i].value;
          if (
            email === "" ||
            /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)
          ) {
            valid = true;
          } else {
            showAlertModal(
              "Die E-Mail ist nicht valide. Bitte geben Sie eine valide E-Mail ein.",
            );
            valid = false;
          }
        }
        if (
          !responses[i] ||
          (!responses[i].optionalQuestion && responses[i].value === "")
        ) {
          showAlertModal(
            "Sie haben noch nicht alle Fragen beantwortet. Folgende Frage wurde noch nicht beantwortet: " +
              (i + 1),
          );
          updateState({ highlightItemIndex: i });
          console.log("isRealSurveyStarted", isRealSurveyStarted);
          return;
        } else {
          if (isRealSurveyStarted) {
            responses[i] = {
              ...state.itemResponses[i],
              time_taken: parseFloat(timeTaken),
            };
          } else {
            responses[i] = {
              ...state.itemResponses[i],
              time_taken: 0,
            };
          }
        }
      }

      if (valid) {
        await sendSurveyResponses(responses);
      }
    }
  };

  //set document title when component mounts
  useLayoutEffect(() => {
    if (title) {
      document.title = title;
    }
  }, [title]);

  //the initial survey data fetch
  useEffect(() => {
    (async function () {
      await tryGetSurvey();
    })();
  }, [tryGetSurvey]);

  /**
   * Main effect function to handle auto-submission
   * immediately submits INFO_AUTOSUBMIT items without user clicking
   */
  useEffect(() => {
    if (!Array.isArray(state.items) || state.items.length === 0) {
      console.log("Items array is empty or not loaded yet");
      return;
    }

    console.log("items: " + JSON.stringify(state.items));

    if (state.items[0].type === "INFO_AUTOSUBMIT") {
      const handleAutoSubmit = async () => {
        if (isSubmittingRef.current) {
          return;
        }

        isSubmittingRef.current = true;
        try {
          const autoSubmitResponse = [
            {
              id: state.items[0].id,
              label: "",
              time_taken: 0,
              value: "",
              optionalQuestion: false,
            },
          ];

          console.log(
            "Auto-submit response:",
            JSON.stringify(autoSubmitResponse),
          );

          await sendSurveyResponses(autoSubmitResponse);
        } catch (error) {
          console.error("Auto-submit failed:", error);
        } finally {
          isSubmittingRef.current = false;
        }
      };
      handleAutoSubmit();
      return;
    }

    try {
      initialEmptyResponses(state.items);
      checkGroupOptions(state.items, state.iGroups);
      extractScript(state.items);
    } catch (error) {
      console.error("Initialization failed:", error);
    }
  }, [
    state.items,
    state.iGroups,
    checkGroupOptions,
    extractScript,
    initialEmptyResponses,
    sendSurveyResponses,
  ]);

  /**
   * Navigation protection effect to warn users when leaving the survey
   * Due to the strict mode of reat, components would mount twice in development
   * checkIsRealNavigation() ensures only real navigation is detected.
   */
  useEffect(() => {
    return () => {
      const isRealNav = checkIsRealNavigation();
      console.log(`is real navigation: ${isRealNav}`);
      if (!state.endOfSurvey && isRealNav) {
        showAlertModal(
          "Sie verlassen die Befragung und kehren zur Übersicht zurück. Pausierte Befragungen können Sie jederzeit fortsetzen.",
        );
      }
    };
  }, [checkIsRealNavigation, showAlertModal, state.endOfSurvey]);

  const renderInfo = (index: number, item: SurveyItem) => (
    <div
      className="my-4 p-4 alert alert-secondary bg-light"
      key={index}
      dangerouslySetInnerHTML={{ __html: item.content }}
    />
  );

  /**
   * Renders a horizontal radio Question type
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element containing all options for this question
   */
  const renderQuestionHorizontalRadioEnforced = (
    index: number,
    item: SurveyItem,
  ) => {
    return (
      <Row className={"flex-nowrap overflow-scroll pb-2 mx-3"}>
        {item.response_options &&
          item.response_options.length > 0 &&
          item.response_options.map((option, idx) => {
            return (
              <>
                <div key={idx} className="me-3 d-inline-block flex-shrink-1">
                  {/* make items horizontal through columns */}
                  <Form.Check
                    type="radio"
                    className="pt-1"
                    inline
                    name={String(item.id)}
                    data-name={option.label}
                    key={idx}
                    id={`survey-${item.id}-horizontal-${idx}`}
                    label={option.label}
                    value={option.value}
                    checked={
                      state.itemResponses[index] &&
                      state.itemResponses[index]?.label === option.label &&
                      state.itemResponses[index]?.value === option.value
                    }
                    onChange={(event) => {
                      handleRadioBtn(event, item, index);
                    }}
                    disabled={state.sendingSurvey}
                  />
                </div>
              </>
            );
          })}
      </Row>
    );
  };

  /**
   * Renders some checkboxes (WIP; not done)
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element containing checkboxes
   */
  const renderQuestionCheckbox = (index: number, item: SurveyItem) => {
    return (
      <Row className={"overflow-scroll px-3 ms-2"}>
        {item.response_options &&
          item.response_options.length > 0 &&
          item.response_options.map((option, idx) => {
            return (
              <>
                <Form.Check
                  type="checkbox"
                  inline
                  name={String(item.id)}
                  data-name={option.label}
                  key={idx}
                  id={option.id}
                  label={option.label}
                  value={option.value}
                  onChange={() => {
                    /*TODO: rehacat_frontend#362 - Not implemented */
                  }}
                />
              </>
            );
          })}
      </Row>
    );
  };

  /**
   * Renders a Selection Question type
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element containing a selection of items
   */
  const renderQuestionSelect = (index: number, item: SurveyItem) => {
    return (
      <Form.Select
        value={state.itemResponses[index]?.value ?? ""}
        onChange={(event) => {
          handleQuestionSelect(event, item, index);
        }}
        disabled={state.sendingSurvey}
      >
        <option>Auswählen...</option>
        {item.response_options &&
          item.response_options.length > 0 &&
          item.response_options.map((option, idx) => {
            return (
              <option
                key={`select-${item.id}-${idx}`}
                value={option.value}
                data-name={option.label}
              >
                {option.label}
              </option>
            );
          })}
      </Form.Select>
    );
  };

  /**
   * Renders a text Question type
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element dropdown
   */
  const renderQuestionText = (index: number, item: SurveyItem) => {
    return (
      <Form.Control
        type="text"
        value={state.itemResponses[index]?.value || ""}
        onChange={(event) => {
          handleTextInput(event, item, index);
        }}
        disabled={state.sendingSurvey}
      />
    );
  };

  /**
   * Renders a text-area Question type
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element containing a text field
   */
  const renderQuestionTextArea = (index: number, item: SurveyItem) => {
    return (
      <Form.Control
        as="textarea"
        value={state.itemResponses[index]?.value || ""}
        onChange={(event) => {
          handleTextInput(event, item, index);
        }}
        disabled={state.sendingSurvey}
      />
    );
  };

  /**
   * Renders a vertical Question type
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element containing all options for this question
   */
  const renderQuestionVerticalRadio = (index: number, item: SurveyItem) => {
    console.log(
      `Radio render - index: ${index}, itemResponses[${index}]:`,
      state.itemResponses[index],
    );

    return (
      <>
        {item.response_options &&
          item.response_options.length > 0 &&
          item.response_options.map((option, idx) => {
            return (
              <div
                className="w-100 pb-3"
                key={`radio-vertical-${item.id}-${idx}`}
              >
                <Form.Check
                  type="radio"
                  inline
                  name={String(item.id)}
                  data-name={option.label}
                  key={idx}
                  id={`survey-${item.id}-vertical-${idx}`}
                  label={option.label}
                  checked={
                    (state.itemResponses[index] &&
                      state.itemResponses[index].value === option.value &&
                      state.itemResponses[index].label === option.label) ||
                    false
                  }
                  value={option.value}
                  onChange={(event) => {
                    handleRadioBtn(event, item, index);
                  }}
                  disabled={state.sendingSurvey}
                />
              </div>
            );
          })}
      </>
    );
  };

  /**
   * Renders a range indicator with a numeric input field.
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element containing the range image and an input field.
   */
  const renderQuestionRangeIndicator = (index: number, item: SurveyItem) => {
    console.log("render:", index, state.itemResponses[index]);

    return (
      <>
        <img
          className="p-1"
          src={temperatureScale}
          alt="Temperature Scale"
          width="100px"
          height="auto"
        />
        <input
          type="number"
          min="0"
          max="100"
          step="1"
          aria-label="Range input (0-100)"
          value={state.itemResponses[index]?.value ?? ""}
          onChange={(event) => {
            handleInteger(event, item, index);
          }}
          disabled={state.sendingSurvey}
          onKeyDown={(event) => {
            if (["e", "+", "-"].includes(event.key)) {
              event.preventDefault(); // Prevent non-numeric characters
            }
            handleInteger(event, item, index);
          }}
        />
      </>
    );
  };

  /**
   * Renders a input field that allows double numbers
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element containing a input for double numbers
   */
  const renderQuestionDoublePositive = (index: number, item: SurveyItem) => {
    return (
      <Form.Control
        type="text"
        inputMode="decimal" // mobile optimization
        onChange={(event) => {
          handlePosDouble(event, item, index);
        }}
        disabled={state.sendingSurvey}
      />
    );
  };

  /**
   * Renders a date input field
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element containing a input for dates
   */
  const renderQuestionDate = (index: number, item: SurveyItem) => {
    return (
      <DatePicker
        className="form-control"
        dateFormat="dd.MM.yyyy"
        selected={state.date || null}
        placeholderText="Datum auswählen"
        onChange={(date) => {
          handleDate(date, item, index);
        }}
        disabled={state.sendingSurvey}
      />
    );
  };

  /**
   * Renders a date input field
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element containing a input for dates
   */
  const renderQuestionInteger = (index: number, item: SurveyItem) => {
    return (
      <Form.Control
        className="form-control"
        type="number"
        min="0"
        max="2147483647"
        pattern="[0-9]*"
        value={state.itemResponses[index]?.value || ""} // Controlled by state
        onChange={(event) => {
          handleInteger(event, item, index);
        }}
        disabled={state.sendingSurvey}
        onKeyDown={(event) => {
          handleInteger(event, item, index);
        }}
      />
    );
  };

  /**
   * Renders a date input field
   *
   * @param {number} index - The index of the current survey item.
   * @param {SurveyItem} item - The survey item associated with the input.
   * @returns {JSX.Element} A JSX element containing a input for dates
   */
  const renderEmailNotification = (index: number, item: SurveyItem) => {
    return (
      <>
        <Form.Control
          type="email"
          placeholder="max@mustermann.de"
          value={state.itemResponses[index]?.value || ""}
          onChange={(event) => {
            handleTextInput(event, item, index);
          }}
          disabled={state.sendingSurvey}
        />
        <Button
          className="m-2"
          onClick={(event) => {
            handleAtSymbol(event);
          }}
        >
          @ einfügen
        </Button>
      </>
    );
  };

  const renderQuestion = (index: number, item: SurveyItem) => {
    switch (item.type) {
      case "QUESTION_HORIZONTAL_RADIO":
        // TODO: QUESTION_HORIZONTAL_RADIO is never visible
        // => css rules that were deleted, by removing css files (survey.css):
        // radio-grid-table-element, question-highlighted (->red background),
        // need to be updated (css + logic) and moved to its own method
        return (
          <div className="radio-horizontal">
            {state.iGroups &&
              state.iGroups.length > 0 &&
              state.iGroups.map((itemgroup, i) => {
                return i === index && itemgroup.group ? (
                  <div key={itemgroup.id} className="radio-grid-table-element ">
                    {itemgroup.head ? (
                      <div
                        key={i}
                        className="radio-grid-table-head radio-grid-table without-horizontal-scroll"
                      >
                        <div className="radio-label" key="-1"></div>
                        {item.response_options &&
                          item.response_options.length > 0 &&
                          item.response_options.map((option, idx) => {
                            return (
                              // TODO: remove eslint disables, see issue rehacat_frontend#335
                              // eslint-disable-next-line react/jsx-key
                              <div className="radio-col">
                                <label className="radio-custom" key={idx}>
                                  {option.label}
                                </label>
                              </div>
                            );
                          })}
                      </div>
                    ) : null}
                    {/* Question Label */}
                    <div
                      className="radio-label"
                      dangerouslySetInnerHTML={{
                        __html: item.content,
                      }}
                    ></div>
                    <div
                      className={`without-horizontal-scroll radio-grid-table grid-table${
                        index === state.highlightItemIndex
                          ? " question-highlighted"
                          : ""
                      }`} /*{
                                  i % 2 === 0
                                    ? "align-horizontal-scroll radio-grid-table grid-table"
                                    : "align-horizontal-scroll radio-grid-table"
                                }*/
                    >
                      <div
                        className="radio-label"
                        dangerouslySetInnerHTML={{
                          __html: item.content,
                        }}
                        key={i}
                      ></div>
                      {item.response_options &&
                        item.response_options.length > 0 &&
                        item.response_options.map((option, idx) => {
                          return (
                            // TODO: remove eslint disables, see issue rehacat_frontend#297
                            // eslint-disable-next-line react/jsx-key
                            <div className="radio-col">
                              {/* TODO: remove eslint disables, see issue rehacat_frontend#297 */}
                              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                              <label className="radio-custom">
                                <Form.Check
                                  /*className="btn-lg"*/
                                  type="radio"
                                  aria-label="radio-btn"
                                  inline
                                  name={String(item.id)}
                                  data-name={option.label}
                                  key={idx}
                                  id={option.id}
                                  value={option.value}
                                  checked={
                                    state.itemResponses[index] &&
                                    state.itemResponses[index].value ===
                                      option.value
                                  }
                                  onChange={(event) => {
                                    handleRadioBtn(event, item, index);
                                  }}
                                  disabled={state.sendingSurvey}
                                />
                              </label>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : i === index && itemgroup.group ? (
                  <div key={i} className="radio-normal-element">
                    <Form.Label
                      key={index}
                      dangerouslySetInnerHTML={{
                        __html: item.content,
                      }}
                    ></Form.Label>
                    <div className="align-horizontal-scroll">
                      {item.response_options &&
                        item.response_options.length > 0 &&
                        item.response_options.map((option, idx) => {
                          return (
                            // TODO: remove eslint disables, see issue rehacat_frontend#297
                            // eslint-disable-next-line react/jsx-key
                            <div className="radio-col">
                              {/* TODO: remove eslint disables, see issue rehacat_frontend#335 */}
                              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                              <label className="radio-custom">
                                <Form.Check
                                  type="radio"
                                  /*className="btn-lg"*/
                                  inline
                                  name={String(item.id)}
                                  data-name={option.label}
                                  key={idx}
                                  id={option.id}
                                  label={option.label}
                                  value={option.value}
                                  checked={
                                    state.itemResponses[index] &&
                                    state.itemResponses[index].value ===
                                      option.value
                                  }
                                  onChange={(event) => {
                                    handleRadioBtn(event, item, index);
                                  }}
                                  disabled={state.sendingSurvey}
                                />
                              </label>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : null;
              })}
          </div>
        );

      case "QUESTION_HORIZONTAL_RADIO_ENFORCED":
        return renderQuestionHorizontalRadioEnforced(index, item);
      case "QUESTION_CHECKBOX":
        return renderQuestionCheckbox(index, item);
      case "QUESTION_VERTICAL_RADIO":
        return renderQuestionVerticalRadio(index, item);
      case "QUESTION_SELECT":
        return renderQuestionSelect(index, item);
      case "QUESTION_RANGE_INDICATOR":
        return renderQuestionRangeIndicator(index, item);
      case "QUESTION_TEXT":
      case "QUESTION_TEXT_OPTIONAL":
        return renderQuestionText(index, item);
      case "QUESTION_DATE":
        return renderQuestionDate(index, item);
      case "QUESTION_INTEGER":
        return renderQuestionInteger(index, item);
      case "QUESTION_DOUBLE_POSITIVE":
        return renderQuestionDoublePositive(index, item);
      case "EMAIL_NOTIFICATION_PRE":
      case "EMAIL_NOTIFICATION_INTERMEDIATE":
      case "EMAIL_NOTIFICATION_POST":
      case "EMAIL_NOTIFICATION_FOLLOW_UP":
        return renderEmailNotification(index, item);
      case "QUESTION_TEXT_AREA":
      case "QUESTION_TEXT_AREA_OPTIONAL":
        return renderQuestionTextArea(index, item);
      default:
        return (
          <div className="mx-2 alert alert-danger text-center">
            Der Fragentyp ist nicht implementiert. Bitte wenden Sie sich an
            Ihren Administrator.
          </div>
        );
      // TODO: Not implemented types:
      // - QUESTION_SUGGESTION (500: server-error)
      // - ARBITRARY_STRING_INPUT_FIELD (500: server-error)
      // - ARBITRARY_NUMBER_INPUT_FIELD (500: server-error)
    }
  };

  // dangerouslySetInnerHTML is needed because we get HTML code as string
  return (
    <div id="body">
      {/* navigation bar (bar on the top) */}
      <NavigationBar />
      <div className="container-fluid w-100 pt-4">
        <div id="main-content" className={"px-0 px-sm-5 px-md-5 mx-md-5"}>
          {/* Title */}
          {state.surveyDimension?.title ? (
            <h4>{state.surveyDimension?.title}</h4>
          ) : null}

          {/* Description */}
          {state.surveyDimension?.paragraph ? (
            <div
              className="survey-description-text mt-3"
              dangerouslySetInnerHTML={{
                __html: state.surveyDimension?.paragraph,
              }}
            />
          ) : null}
          {/* Formular */}
          <Form noValidate onSubmit={handleSurveyResponses} className="mb-5">
            {state.items?.length > 0 &&
              state.items.map((item, index) => {
                return ["INFO", "INFO_AUTOSUBMIT"].includes(item.type) ? (
                  renderInfo(index, item)
                ) : (
                  <Form.Group
                    key={index}
                    className={`my-4 p-4 alert ${
                      index === state.highlightItemIndex
                        ? "alert-danger"
                        : "alert-secondary bg-light"
                    } text-dark`}
                  >
                    {/* TODO: Why is QUESTION_HORIZONTAL_RADIO excempt? */}
                    {item.type !== "QUESTION_HORIZONTAL_RADIO" ? (
                      <Form.Label
                        key={index}
                        dangerouslySetInnerHTML={{ __html: item.content }}
                      />
                    ) : null}
                    <div className="m-2">{renderQuestion(index, item)}</div>
                  </Form.Group>
                );
              })}
            {/* Footer */}
            {state.surveyDimension?.footer ? (
              <div
                className="font-dim-gray m-1 mt-4"
                dangerouslySetInnerHTML={{
                  __html: state.surveyDimension?.footer,
                }}
              />
            ) : null}
            {/* zurück/ back & weiter/ next form buttons */}
            <OverlayTrigger
              overlay={
                <Tooltip className="tooltip">
                  Sie können nicht zur vorherigen Frage zurückkehren.
                </Tooltip>
              }
            >
              <Button
                aria-describedby="tooltipBack"
                variant="outline"
                className="mx-1"
              >
                Zurück
              </Button>
            </OverlayTrigger>
            <Button
              disabled={
                // items.length === 0 ||
                state.sendingSurvey ? true : false
              }
              type="submit"
              className="mx-1"
              variant="primary"
            >
              {" "}
              {state.sendingSurvey ? (
                <Spinner
                  animation="border"
                  role="status"
                  // className="loadingSpinner"
                >
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              ) : (
                "Weiter"
              )}
            </Button>
          </Form>
          <Modal
            backdrop="static"
            show={state.waitModalOpen}
            onHide={closeModal}
          >
            <ModalBody>
              Ihre Daten werden übermittelt. Bitte haben Sie einen Moment
              Geduld.
              <ProgressBar animated now={state.progressBarStatus} />
            </ModalBody>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default RunSurvey;
